import DateDisplay from '@app/src/components/DateDisplay'
import RatingBar from '@app/src/components/RatingBar'
import type Assessment from '@app/src/types/assessment'
import type { Provider } from '@app/src/types/organizations'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import AssessmentAnswerRow from './AssessmentAnswerRow'
import type { AnswerSection } from './assessmentAnswersLogic'

interface AssessmentAnswersProps {
  assessment: Assessment
  sections: AnswerSection[]
  provider: Provider | undefined
  showScoring: boolean
  maxRowsForLongAnswers: number
}

const AssessmentAnswers: React.FC<AssessmentAnswersProps> = ({
  assessment,
  sections,
  provider,
  showScoring,
  maxRowsForLongAnswers,
}) => {
  const { formatMessage } = useIntl()
  const isBaseline = assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment
  const levelsCount = assessment.assessmentTemplate.levels
  const visibleSections = sections.filter(section => section.rows.length > 0)

  const renderRows = (section: AnswerSection) =>
    section.rows.map(row => (
      <AssessmentAnswerRow
        key={row.item.id}
        row={row}
        provider={provider}
        isBaseline={isBaseline}
        showScoring={showScoring}
        maxRowsForLongAnswers={maxRowsForLongAnswers}
      />
    ))

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">
            {formatMessage({ id: 'baselineAssessment.providerPage.answers' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'baselineAssessment.providerPage.resultsAssessedOn' })}{' '}
            <DateDisplay value={assessment.updatedAt ?? assessment.createdAt} />
          </Typography>
        </Stack>
        {visibleSections.map(section =>
          section.name ? (
            <Stack key={section.key} spacing={2} p={2} bgcolor="grey.50" borderRadius={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{section.name}</Typography>
                <RatingBar level={section.level} levelsCount={levelsCount} preview />
              </Stack>
              <Stack spacing={2}>{renderRows(section)}</Stack>
            </Stack>
          ) : (
            <Stack key={section.key} spacing={2}>
              {renderRows(section)}
            </Stack>
          ),
        )}
      </Stack>
    </Paper>
  )
}

export default AssessmentAnswers
